import { createValidate } from 'typia'
import { validateGuard } from 'components/shared/parsing/parsing.js'
import { parseStockDTO, StockDTO } from 'components/ordering/configurator/api/dtos/stock-dto.js'

export type IProductDTO = {
  id: number
  name: string
  name_with_attributes: string
  slug: string
  code: string
  oem_code: string | null
  img: unknown
  description: string | null
  description2: string | null
  description3: string | null
  picture: Record<string, unknown>
  catalogs: number[]
  endpoint: string | null
  group: unknown
  order_qty: unknown
  order_default_qty: unknown
  order_minimum_qty: unknown
  order_maximum_qty: unknown
  dimension_product: unknown
  dimension: unknown
  tree: number | null
  tree_name: string
  brand: string
  default_uom: string
  sale_uom: number
  out_assortment: unknown
  delivery_days: number | null
  min_stock_backorder: number
  min_stock: number
  delivery_service_id: number | null
  stock: unknown
  assets: unknown
  videos: unknown[]
  alt_images: unknown[]
  external_links: unknown[]
  status: Record<string, unknown>
  consumer_price_no_format: number | null
  sale_price_no_format: number | null
  sale_price_in_cents: number | null
  currency_symbol: string | null
  display_as_discount: boolean | null
  sale_price_taxed: unknown
  discount_price_data: unknown
  customer_price: unknown
  consumer_price: unknown
  lowest_price: unknown
  lowest_price_no_format: unknown
  customer_price_uom: unknown
  consumer_price_uom: unknown
  sale_price: unknown
  price_per_price_uom: unknown
  price_type: string | null
  price_info: unknown[]
  list_price: unknown
  erp_prices: unknown
  price_cost_rules: unknown
  delivery_indication: Record<string, unknown>
  delivery_indication_shop: Record<string, unknown>
  external_configurator: string | null
  external_configurator_data: Record<string, unknown>
  selected?: boolean
}

export class ProductDTO {
  id: number
  name: string
  nameWithAttributes: string
  slug: string
  code: string
  oemCode: string | null
  img: unknown
  description: string | null
  description2: string | null
  description3: string | null
  picture: Record<string, unknown>
  catalogs: number[]
  endpoint: string | null
  group: unknown
  orderQty: unknown
  orderDefaultQty: unknown
  orderMinimumQty: unknown
  orderMaximumQty: unknown
  dimensionProduct: unknown
  dimension: unknown
  tree: number | null
  treeName: string
  brand: string
  defaultUom: string
  saleUom: number
  outAssortment: unknown
  deliveryDays: number | null
  minStockBackorder: number
  minStock: number
  deliveryServiceId: number | null
  stock: StockDTO | null
  assets: unknown
  videos: unknown[]
  altImages: unknown[]
  externalLinks: unknown[]
  status: Record<string, unknown>
  consumerPriceNoFormat: number | null
  salePriceNoFormat: number | null
  salePriceInCents: number | null
  currencySymbol: string | null
  displayAsDiscount: boolean | null
  salePriceTaxed: unknown
  discountPriceData: unknown
  customerPrice: unknown
  consumerPrice: unknown
  lowestPrice: unknown
  lowestPriceNoFormat: unknown
  customerPriceUom: unknown
  consumerPriceUom: unknown
  salePrice: unknown
  pricePerPriceUom: unknown
  priceType: string | null
  priceInfo: unknown[]
  listPrice: unknown
  erpPrices: unknown
  priceCostRules: unknown
  deliveryIndication: Record<string, unknown>
  deliveryIndicationShop: Record<string, unknown>
  externalConfigurator: string | null
  externalConfiguratorData: Record<string, unknown>
  selected: boolean | undefined

  constructor(data: IProductDTO) {
    this.id = data.id
    this.name = data.name
    this.nameWithAttributes = data.name_with_attributes
    this.slug = data.slug
    this.code = data.code
    this.oemCode = data.oem_code
    this.img = data.img
    this.description = data.description
    this.description2 = data.description2
    this.description3 = data.description3
    this.picture = data.picture
    this.catalogs = data.catalogs
    this.endpoint = data.endpoint
    this.group = data.group
    this.orderQty = data.order_qty
    this.orderDefaultQty = data.order_default_qty
    this.orderMinimumQty = data.order_minimum_qty
    this.orderMaximumQty = data.order_maximum_qty
    this.dimensionProduct = data.dimension_product
    this.dimension = data.dimension
    this.tree = data.tree
    this.treeName = data.tree_name
    this.brand = data.brand
    this.defaultUom = data.default_uom
    this.saleUom = data.sale_uom
    this.outAssortment = data.out_assortment
    this.deliveryDays = data.delivery_days
    this.minStockBackorder = data.min_stock_backorder
    this.minStock = data.min_stock
    this.deliveryServiceId = data.delivery_service_id
    this.stock = parseStockDTO(data.stock)
    this.assets = data.assets
    this.videos = data.videos
    this.altImages = data.alt_images
    this.externalLinks = data.external_links
    this.status = data.status
    this.consumerPriceNoFormat = data.consumer_price_no_format
    this.salePriceNoFormat = data.sale_price_no_format
    this.salePriceInCents = data.sale_price_in_cents
    this.currencySymbol = data.currency_symbol
    this.displayAsDiscount = data.display_as_discount
    this.salePriceTaxed = data.sale_price_taxed
    this.discountPriceData = data.discount_price_data
    this.customerPrice = data.customer_price
    this.consumerPrice = data.consumer_price
    this.lowestPrice = data.lowest_price
    this.lowestPriceNoFormat = data.lowest_price_no_format
    this.customerPriceUom = data.customer_price_uom
    this.consumerPriceUom = data.consumer_price_uom
    this.salePrice = data.sale_price
    this.pricePerPriceUom = data.price_per_price_uom
    this.priceType = data.price_type
    this.priceInfo = data.price_info
    this.listPrice = data.list_price
    this.erpPrices = data.erp_prices
    this.priceCostRules = data.price_cost_rules
    this.deliveryIndication = data.delivery_indication
    this.deliveryIndicationShop = data.delivery_indication_shop
    this.externalConfigurator = data.external_configurator
    this.externalConfiguratorData = data.external_configurator_data
    this.selected = data.selected
  }
}

const validateProductDTO = createValidate<IProductDTO>()

export const parseDefiniteProductDTO = (data: unknown): ProductDTO => {
  validateGuard(validateProductDTO, data, 'ProductDTO')
  return new ProductDTO(data)
}
export const parseMaybeProductDTO = (data: unknown): ProductDTO | undefined => {
  if (data === undefined) return undefined
  return parseDefiniteProductDTO(data)
}
